import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import { Box, IconButton, makeStyles, TextField, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";
import { useNotify, useRefresh } from "react-admin";
import TooltipPopover from "../../../../components/TooltipPopover";

import Loader from "../../../../components/Loader";
import { firestore } from "../../../../init/firebase";
import { AppResource } from "../../../../types/resource.types";
import { applyCreateTimestamp } from "../../../../utils/timestamp";
import { RequestStatus } from "../types";
import { fetchRequestChangedTimes } from "../utils/requestChangedTimes";
import { requestFetchedData } from "../utils/requestData";
import useIsEntertainment from "../../../../hooks/useIsEntertainment";

const useStyles = makeStyles({
  requestDateStyle: {
    marginRight: "3px",

    "& input": {
      fontSize: "14px",
      padding: "8px 5px",
    },
  },
});

const formatDate = (date: any = dayjs(), format = "DD-MM-YYYY"): string => dayjs(date).format(format);

const RequestDate: FC<any> = ({ record, source }) => {
  const { isEntertainment } = useIsEntertainment();
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();

  const [requestDate, setRequestDate] = useState<Date | null>(record[source] ? record[source].toDate() : null);
  const [value, setValue] = useState<any>(record[source] ? formatDate(record[source].toDate()) : null);
  const [loading, setLoading] = useState(false);
  const [loadingFetching, setLoadingFetching] = useState(false);
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [isThereChangedDate, setIsThereChangedDate] = useState(true);

  const isPendingReqORConfirmed = () =>
    record.status === RequestStatus.pending || record.status === RequestStatus.confirmed;

  const isSameRequestDate = () => formatDate(requestDate) === formatDate(record[source].toDate());

  const handleSelectRequestDate = (e: any) => {
    e.stopPropagation();
    setRequestDate(new Date(e.target.value));
  };

  const handleCancelEditMode = () => {
    setIsInEditMode(false);
    setValue(formatDate(record[source].toDate()));
  };

  const handleSubmit = async () => {
    setLoading(true);

    const sn = await requestFetchedData(record.ref);
    if (sn?.status === RequestStatus.pending || sn?.status === RequestStatus.confirmed) {
      try {
        await firestore.collection(`${record.ref.path}/${AppResource.changedTimes}`).add(
          applyCreateTimestamp({
            status: "pending",
            date: requestDate,
          })
        );

        setLoading(false);
        setIsInEditMode(false);
        setValue(formatDate(requestDate));
        refresh();
        notify("Date has been created successfully", "success");
      } catch (ex: any) {
        console.log(ex);
        setValue(formatDate(record[source].toDate()));
        setLoading(false);
        setIsInEditMode(false);
        notify("Error on creating date", "success");
      }
    } else {
      notify("Request has been expired", "error");
    }
  };

  useEffect(() => {
    (async function () {
      const fetchedData: any = await fetchRequestChangedTimes(record);

      if (fetchedData && fetchedData.date) {
        setValue(formatDate(fetchedData.date.toDate()));
        setIsThereChangedDate(false);
        setLoadingFetching(false);
      }
    })();
  }, []);

  return (
    <>
      {value ? (
        <Loader loading={loadingFetching}>
          {!isInEditMode ? (
            <Box display="flex" alignItems="center">
              <Typography variant="body2" style={{ minWidth: "75px" }}>
                {value}
              </Typography>
              {isPendingReqORConfirmed() && isThereChangedDate && isEntertainment && (
                <TooltipPopover message="Edit">
                  <IconButton
                    onClick={() => {
                      setIsInEditMode(true);
                    }}
                    style={{ marginInline: 2 }}
                  >
                    <EditIcon fontSize="small" style={{ fontSize: "1rem", color: "#21C5FD" }} />
                  </IconButton>
                </TooltipPopover>
              )}
            </Box>
          ) : (
            <Box display="flex" alignItems="center">
              <TextField
                type="date"
                variant="outlined"
                className={classes.requestDateStyle}
                onChange={handleSelectRequestDate}
                onClick={(e: any) => e.stopPropagation()}
                defaultValue={formatDate(requestDate, "YYYY-MM-DD")}
                disabled={loading}
                inputProps={{
                  min: formatDate(dayjs(), "YYYY-MM-DD"),
                  required: true,
                }}
              />

              <Box display="flex" alignItems="center">
                <Loader loading={loading} size={18}>
                  <TooltipPopover message={isSameRequestDate() ? "Same day, change it!" : "Save"}>
                    <IconButton onClick={handleSubmit} size="small" disabled={isSameRequestDate()}>
                      <CheckIcon fontSize="small" color="primary" />
                    </IconButton>
                  </TooltipPopover>
                </Loader>

                <TooltipPopover message="Cancel">
                  <IconButton disabled={loading} size="small" onClick={handleCancelEditMode}>
                    <CloseIcon fontSize="small" color="error" />
                  </IconButton>
                </TooltipPopover>
              </Box>
            </Box>
          )}
        </Loader>
      ) : (
        "N/A"
      )}
    </>
  );
};

export default RequestDate;
